import {
  dapiFetch,
  dapiFetchCache,
  ErrorFetch,
  IDapiCacheRequest,
  IDapiCacheResponse,
  IDapiRequest,
  IDapiResponse,
} from "@origin-digital/daxi";
import { logger } from "@origin-digital/reporting-client";
import { redactObject } from "./redact.util";

/**
 * Attempts to log to sumo, ignoring any failures and rethrow so that existing error handling by callers still occur
 * @param e
 */
const quietlyLogErrorToSumo = <F = undefined>(error: ErrorFetch<F>) => {
  try {
    logger.error(
      error.message ||
        error.displayMessage ||
        "API call failed with no specified message",
      {
        errorData: redactObject(error),
      }
    );
  } catch (e) {
    console.log("error logging api error to sumo", e);
  }
  throw error;
};

export const is5xxError = (error: ErrorFetch) => {
  const errorPattern = /5\d{2}/;
  const status = error?.fetchResponse?.status?.toString() ?? "";
  return (status && errorPattern.test(status)) || false;
};

/**
 * Calls dapiFetchCache in DAXI.
 * ODTraceId and BusinessTraceid is inserted.
 *
 * Catches the errors, logs it in sumo then rethrows the  error.
 *
 * @param url
 * @param config
 */
export const dapiFetchCacheWithSumoErrorLogging = <M>(
  url: string,
  config: IDapiCacheRequest = { ttlInMin: 30 },
  attempts = 1
): Promise<IDapiCacheResponse<M>> => {
  // Always set the traceId to the session traceId.
  return dapiFetchCache<M>(url, config).catch((error) => {
    if (attempts > 1 && is5xxError(error)) {
      return dapiFetchCacheWithSumoErrorLogging(url, config, attempts - 1);
    } else {
      return quietlyLogErrorToSumo(error);
    }
  });
};

/**
 * Calls dapiFetch in DAXI.
 * ODTraceId and BusinessTraceid is inserted.
 *
 * Catches the errors, logs it in sumo then rethrows the  error.
 *
 * @param url
 * @param config
 */
export const dapiFetchWithSumoErrorLogging = <M, F = undefined>(
  url: string,
  config: IDapiRequest = {}
): Promise<IDapiResponse<M>> => {
  // Always set the traceId to the session traceId.
  return dapiFetch<M, F>(url, config).catch(quietlyLogErrorToSumo);
};
